import React, { ReactNode } from 'react'
import { AdCreative, formatCallPhoneNumber, isCallCreative } from '@marketing-milk/interfaces'
import { CreativeVerdictBadge, hasClientApproval } from './CreativeVerdictBadge'
import { CreativeMedia } from './CreativeMedia'

type Props = {
  creative: AdCreative
  selected?: boolean
  /** Dims the row and blocks the click outright. */
  disabled?: boolean
  /**
   * Why this ad is withheld from being picked, named on the row. Separate from
   * `disabled` because a withheld ad that is already attached still has to be
   * clickable — see `CreativeCard`.
   */
  withheldReason?: string
  /**
   * Trailing content, supplied by whoever is using the row. The campaign picker
   * puts its ad-set letter here; screens where the letter means nothing pass
   * nothing and get an empty slot.
   */
  trailing?: ReactNode
  /** Off where the ads are already grouped under their form, so the column would only repeat it. */
  showLeadForm?: boolean
  onPreview?: (adCreative: AdCreative) => void
  onSelect: (adCreative: AdCreative) => void
}

export const CreativeListRow = ({
  creative,
  selected,
  disabled,
  withheldReason,
  trailing,
  showLeadForm = true,
  onPreview,
  onSelect,
}: Props) => {
  return (
    <div
      className={`flex items-center gap-4 w-full px-4 py-2 border-l-4 transition-colors duration-150 ${
        disabled || withheldReason ? 'opacity-50' : ''
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
        selected ? 'bg-purple-50 border-l-purple-600' : 'border-l-transparent hover:bg-gray-50'
      }`}
      data-testid={creative.name}
      onClick={() => !disabled && onSelect(creative)}
    >
      <input
        type="checkbox"
        className="flex-shrink-0 h-4 w-4 accent-purple-600 cursor-pointer disabled:cursor-not-allowed"
        checked={Boolean(selected)}
        disabled={disabled}
        aria-label={`Select ${creative.name}`}
        // The whole row is the click target; the box is a target and a readout, not a
        // second handler — without this React reports it as an uncontrolled input.
        onChange={() => undefined}
      />

      <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden">
        <CreativeMedia
          creative={creative}
          className="h-14 w-14 bg-gray-200 text-[6px] leading-none text-center px-0.5"
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{creative.name}</p>
        <p className="text-sm text-gray-500 truncate">{creative.linkHeadline || '—'}</p>
        {withheldReason ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
            <i className="fa fa-ban" aria-hidden="true" />
            {withheldReason}
          </span>
        ) : (
          hasClientApproval(creative) && <CreativeVerdictBadge />
        )}
      </div>

      {/* Written out rather than shown as an icon with the name in a tooltip: checking a
          batch of fifty against their forms would otherwise be fifty hovers. */}
      {showLeadForm && (
        <div className="flex-shrink-0 w-64 min-w-0">
          {creative.leadCardUsed && (
            <p className="text-sm text-purple-700 font-medium truncate">
              {creative.leadCardUsed.formName}
            </p>
          )}
          {/* Written out for the same reason as the form name: checking a batch of call ads dial
              the right number shouldn't take a hover each. */}
          {isCallCreative(creative) && (
            <p className="text-sm text-purple-700 font-medium truncate">
              Call Now · {formatCallPhoneNumber(creative.callPhoneNumber) ?? 'no number'}
            </p>
          )}
        </div>
      )}

      {/* Wider when it also carries Preview, so "Preview" and "Published" sit on one line. */}
      <div
        className={`flex flex-shrink-0 items-center justify-end gap-2 ${
          onPreview ? 'w-44' : 'w-24'
        }`}
      >
        {onPreview && (
          <button
            type="button"
            aria-label={`Preview ${creative.name}`}
            className="rounded-md px-2 py-1 text-sm font-semibold text-mm-primary-700 hover:bg-mm-primary-50"
            onClick={event => {
              event.stopPropagation()
              onPreview(creative)
            }}
          >
            Preview
          </button>
        )}
        {disabled ? (
          <span className="text-sm font-semibold text-gray-500">Published</span>
        ) : (
          trailing
        )}
      </div>
    </div>
  )
}
